import type { Translations } from '../en/index.js';

export const nl: Translations = {
  common: {
    nav: {
      work: 'Werk',
      approach: 'Aanpak',
      about: 'Over mij',
      hire: 'Huur mij in',
      menu: 'Menu',
    },
    localeLabel: 'Taal',
    skipToContent: 'Naar de inhoud',
    portraitAlt: 'Ahmad El Masri',
    footer: {
      rights: '© 2026 Ahmad El Masri · Eindhoven, NL',
      photography: 'Portretten door {{photographer}}',
      privacy:
        'Deze site telt bezoeken met zelf gehoste analytics. Geen cookies, geen tracking over dagen heen, en niets dat u identificeert.',
    },
    mobileBar: {
      status: 'Beschikbaar voor lead- en adviesopdrachten',
      cta: 'Huur mij in',
    },
  },

  hero: {
    availability: 'Eindhoven · beschikbaar voor lead- en adviesopdrachten',
    nameLine1: 'Ahmad',
    nameLine2: 'El Masri',
    lede: 'Tien jaar rommelige machinedata omgezet in systemen waar mensen op bouwen.',
  },

  stats: {
    throughput: 'berichten door de broker die ik herbouwde',
    database: 'snellere database, dezelfde hardware',
    deploy: 'om te deployen, was een hele dag',
    engineers: 'engineers geleid over drie producten',
  },

  work: {
    titleLine1: 'Geselecteerd',
    titleLine2: 'werk',
    lede: 'Drie systemen, elk een belastingprobleem dat ophield er een te zijn.',
    cases: {
      'sg-wireless': {
        title: 'Een miljoen berichten per seconde, met ruimte over',
        body: 'De broker raakte verzadigd lang voordat de apparaatvloot dat deed. Ik maakte broker en proxy stateless zodat ze op aanvraag schalen, scheidde ingest van queryverkeer, en herindexeerde naar hoe de data werkelijk werd gelezen.',
      },
      withthegrid: {
        title: 'Weten welk netwerkonderdeel als volgende uitvalt',
        body: 'Leidde het Asset Monitoring Platform en het team erachter — productintentie vertalen naar een architectuur die standhoudt naarmate het aantal assets groeit.',
        outcome: 'Geschaald zonder herbouw.',
      },
      pycom: {
        title: 'Twintig engineers, drie roadmaps, één pijplijn',
        body: 'Begonnen als engineer, vertrokken als leider van de groep. We leverden op tijd door het saaie werk te automatiseren — deploys gingen van een hele dag naar minder dan een uur.',
        outcome: 'Drie roadmaps, allemaal op schema.',
      },
    },
  },

  approach: {
    title: 'Hoe ik werk',
    lede: 'Vier gewoontes die een echte deadline overleven.',
    habits: {
      measure: {
        title: 'Meet voordat je beweegt',
        body: 'Zoek het echte knelpunt voordat je de architectuur aanraakt. De helft van de tijd blijkt "we moeten herbouwen" een index te zijn.',
      },
      boring: {
        title: 'Saai waar het telt',
        body: 'Bewezen gereedschap in het kritieke pad. Bewaar het nieuwe voor plekken waar falen goedkoop en omkeerbaar is.',
      },
      slices: {
        title: 'Lever in plakjes',
        body: 'Kleine omkeerbare stappen achter een flag. De migratie die niemand merkte is de migratie die werkte.',
      },
      team: {
        title: 'Laat het team sterker achter',
        body: 'Vastgelegde beslissingen, gereviewde code, en mensen die het prima zonder mij draaiende houden.',
      },
    },
  },

  about: {
    whereTitle: 'Waar ik heb gewerkt',
    beyondTitle: 'Buiten het werk',
    roles: {
      terramira: 'Software Engineer, geospatial',
      withthegrid: 'Senior Engineer & Teamlead',
      'sg-wireless': 'Senior Engineer & Teamlead',
      pycom: 'Engineer → Teamlead',
      iss: 'Beiroet → Muscat',
    },
    beyond: {
      mentoring: {
        title: 'Mentorschap',
        body: 'Udacity-projectbeoordelaar, Pluralsight-mentor — vier jaar andermans code reviewen.',
      },
      writing: {
        title: 'Schrijven',
        body: 'Over schalen, teamleiding en de saaie kanten van betrouwbaarheid.',
      },
      languages: {
        title: 'Talen',
        body: 'Arabisch, Engels, Nederlands, Frans',
      },
      education: {
        title: 'Opleiding',
        body: 'MSc Nanotechnologie, Grenoble · B.Eng, Beirut Arab University',
      },
    },
  },

  hire: {
    titleLine1: 'Laten we iets',
    titleLine2: 'degelijks bouwen',
    lede: 'Leadrollen, contractwerk, of een tweede mening over je architectuur.',
    github: 'GitHub',
    linkedin: 'LinkedIn',
    cv: 'CV (PDF)',
  },

  offers: {
    architecture_review: {
      eyebrow: 'Meest gevraagd',
      title: 'Architectuurreview',
      body: 'Twee weken. Ik lees de code, spreek je engineers, en lever een geprioriteerd plan op dat je zonder mij kunt uitvoeren.',
      terms: 'Vaste prijs · vanaf €6k',
    },
    contract_lead: {
      eyebrow: 'Handen uit de mouwen',
      title: 'Contract lead',
      body: 'Twee tot zes maanden binnen je team — architectuur, het moeilijke werk, en mentorschap terwijl we leveren.',
      terms: 'Dagtarief · in overleg',
    },
    full_time: {
      eyebrow: 'Lange termijn',
      title: 'Vaste rol',
      body: 'Lead of staff engineering in Nederland of EU-remote. Stuur de rol en het team dat je wilt laten bouwen.',
      terms: 'Momenteel niet beschikbaar',
    },
  },

  form: {
    title: 'Stuur je aanvraag',
    lede: 'Niets meer dan dit — geen agendalinks, geen formulieren achter formulieren.',
    engagementLegend: 'Wat voor werk',
    engagement: {
      architecture_review: 'Architectuurreview',
      contract_lead: 'Contract lead',
      full_time: 'Vaste rol',
      talk_workshop: 'Talk / workshop',
    },
    name: 'Naam',
    namePlaceholder: 'Je naam',
    email: 'E-mail',
    emailPlaceholder: 'jij@bedrijf.nl',
    company: 'Bedrijf',
    companyOptional: '— optioneel',
    companyPlaceholder: 'Waar je werkt',
    problem: 'Wat is het probleem?',
    problemPlaceholder: 'Wat bouw je, en wat staat in de weg?',
    when: 'Wanneer',
    budget: 'Budget',
    budgetOptional: '— optioneel',
    timing: {
      immediately: 'Direct',
      within_a_month: 'Binnen een maand',
      within_a_quarter: 'Binnen een kwartaal',
      exploring: 'Nog aan het verkennen',
    },
    budgets: {
      prefer_to_discuss: 'Liever in overleg',
      under_10k: 'Onder €10k',
      from_10k_to_25k: '€10k – €25k',
      from_25k_to_75k: '€25k – €75k',
      over_75k: 'Boven €75k',
    },
    submit: 'Versturen',
    sending: 'Versturen…',
    reassurance: 'Ik antwoord persoonlijk binnen twee werkdagen. NDA op verzoek.',
    dismiss: 'Sluiten',
    turnstileUnavailable:
      'De menselijkheidscontrole kon niet laden. Schakel je adblocker uit, of mail me direct.',
    validation: {
      name: 'Naam is verplicht',
      email: 'Een geldig e-mailadres is verplicht',
      problem: 'Beschrijf het probleem iets uitgebreider',
    },
    status: {
      successTitle: 'Aanvraag verstuurd',
      success: 'Verstuurd. Referentie {{reference}} — ik antwoord binnen twee werkdagen.',
      errorTitle: 'Niet verstuurd',
      verification: 'De menselijkheidscontrole is niet geslaagd. Probeer het opnieuw.',
      rateLimited: 'Te veel aanvragen. Probeer het over een paar minuten opnieuw.',
      error: 'Dat kon niet worden verstuurd. Mail me direct op {{email}}.',
    },
  },

  next: {
    title: 'Wat er daarna gebeurt',
    replyNote: 'Eindhoven · antwoordt binnen 2 dagen',
    steps: {
      read: { title: 'Ik lees het', body: 'Persoonlijk, binnen twee werkdagen.' },
      answer: {
        title: 'Een eerlijk antwoord',
        body: 'Ja, nee, of “hier is iemand die hier beter in is”.',
      },
      call: {
        title: 'Een gesprek van 30 minuten',
        body: 'Alleen als het past. Geen pitchdeck, geen salesgesprek.',
      },
    },
  },
};
